import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/privacy')({
  component: PrivacyPolicy,
});

function PrivacyPolicy() {
  return (
    <div style={pageStyle}>
      <article style={articleStyle}>
        <header>
          <h1 style={h1Style}>Privacy Policy</h1>
          <p style={metaStyle}>Effective date: February 19, 2026</p>
        </header>

        <p>
          Interlucent ("we", "us", or "our") respects your privacy. This Privacy Policy explains what
          information we collect, how we use it, who we share it with, and what rights you have. It
          applies to the Interlucent platform, including the console, APIs, streaming infrastructure,
          and related services (the "Service").
        </p>

        <h2 style={h2Style}>1. Information We Collect</h2>

        <h3 style={h3Style}>Information you provide</h3>
        <ul style={listStyle}>
          <li><strong>Account information</strong> — name, email address, and password when you create an account.</li>
          <li><strong>Organization information</strong> — organization name and membership details.</li>
          <li><strong>Your Content</strong> — application binaries (including Unreal Engine, Unity, and other game engine builds), associated assets, configuration files, and metadata you upload to the Service for hosting and streaming.</li>
          <li><strong>Support requests</strong> — any information you include when contacting us.</li>
        </ul>

        <h3 style={h3Style}>Information collected automatically</h3>
        <ul style={listStyle}>
          <li><strong>Usage data</strong> — pages visited, features used, session duration, and interactions within the console.</li>
          <li><strong>Device and browser data</strong> — browser type, operating system, screen resolution, and IP address.</li>
          <li><strong>Streaming telemetry</strong> — connection quality metrics (latency, packet loss, bitrate) collected during streaming sessions to maintain and improve performance.</li>
          <li><strong>Log data</strong> — server logs including request timestamps, endpoints accessed, and error information.</li>
        </ul>

        <h2 style={h2Style}>2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul style={listStyle}>
          <li>Provide, operate, and maintain the Service;</li>
          <li>Authenticate your identity and secure your account;</li>
          <li>Monitor and improve the performance, reliability, and security of the Service;</li>
          <li>Respond to support requests and communicate with you about your account;</li>
          <li>Detect, prevent, and address fraud, abuse, and technical issues;</li>
          <li>Comply with legal obligations.</li>
        </ul>
        <p>
          We do not sell your personal information. We do not use Your Content for advertising, training
          machine learning models, or any purpose other than providing the Service to you.
        </p>

        <h2 style={h2Style}>3. Application Hosting, Streaming, and Intellectual Property</h2>
        <p>
          The core function of the Service is to store, execute, and stream your applications on cloud
          infrastructure. When you upload an application, we store the binary and its assets in encrypted
          storage, provision compute resources to run it, and deliver the resulting video and audio to
          authorized viewers via secure, token-authenticated WebRTC streams.
        </p>
        <p>
          <strong>Your intellectual property remains yours.</strong> We claim no ownership of Your Content,
          including application binaries, assets, game logic, or any creative work contained therein. We
          do not inspect, analyze, reverse-engineer, or derive works from Your Content. Our access is
          limited to what is strictly necessary to store, execute, and stream it on your behalf.
        </p>
        <p>
          We apply industry best practices to safeguard your application assets, including:
        </p>
        <ul style={listStyle}>
          <li>Encrypted storage at rest and encrypted transmission in transit;</li>
          <li>Isolated execution environments — your applications run in dedicated, sandboxed instances;</li>
          <li>Token-based access control — streams are protected by cryptographically signed admission tokens that you issue and control;</li>
          <li>No persistent caching of streamed output beyond the active session;</li>
          <li>Secure deletion of application assets upon version removal or account termination.</li>
        </ul>
        <p>
          You control who can access your streams through share links, admission tokens, and organization
          membership. We do not grant access to your applications or streams to any third party unless
          explicitly authorized by you or required by law.
        </p>

        <h2 style={h2Style}>4. Third-Party Service Providers</h2>
        <p>
          We share information with third-party providers solely to operate the Service. These providers
          are contractually obligated to protect your data and use it only for the purposes we specify.
        </p>
        <ul style={listStyle}>
          <li><strong>Clerk</strong> — authentication and identity management.</li>
          <li><strong>Cloudflare</strong> — infrastructure, content delivery, security, and streaming.</li>
          <li><strong>Cloud providers (AWS, GCP)</strong> — compute infrastructure for streaming worker pools.</li>
        </ul>
        <p>
          We may also disclose information if required by law, regulation, legal process, or governmental
          request, or to protect the rights, safety, or property of Interlucent, our users, or the public.
        </p>

        <h2 style={h2Style}>5. Cookies and Similar Technologies</h2>
        <p>
          We use cookies and similar technologies for authentication, session management, and remembering
          your preferences. We do not use third-party advertising or tracking cookies.
        </p>
        <ul style={listStyle}>
          <li><strong>Essential cookies</strong> — required for authentication and core functionality. These cannot be disabled.</li>
          <li><strong>Analytics cookies</strong> — help us understand how the Service is used. You may disable these in your browser settings.</li>
        </ul>

        <h2 style={h2Style}>6. Data Retention</h2>
        <p>
          We retain your account information for as long as your account is active. If you delete your
          account, we will delete or anonymize your personal information within 30 days, except where
          retention is required by law or for legitimate business purposes (such as resolving disputes
          or enforcing our agreements). Your Content may be retained for a reasonable period after account
          deletion to allow retrieval, after which it will be permanently deleted.
        </p>
        <p>
          Streaming telemetry and usage data is retained in aggregate form and is not linked to individual
          users after 90 days.
        </p>

        <h2 style={h2Style}>7. Data Security</h2>
        <p>
          We implement industry-standard technical and organizational measures to protect your information
          and Your Content, including encryption in transit (TLS/DTLS) and at rest, access controls,
          isolated execution environments for hosted applications, cryptographically signed access tokens,
          and regular security reviews. However, no method of transmission or storage is completely secure,
          and we cannot guarantee absolute security.
        </p>

        <h2 style={h2Style}>8. International Data Transfers</h2>
        <p>
          Your information may be processed in countries other than your own, including the United States,
          Canada, and the European Union. Where we transfer data internationally, we rely on appropriate
          safeguards such as standard contractual clauses or the data processing agreements of our
          third-party providers.
        </p>

        <h2 style={h2Style}>9. Your Rights</h2>
        <p>
          Depending on your jurisdiction, you may have the following rights regarding your personal
          information:
        </p>
        <ul style={listStyle}>
          <li><strong>Access</strong> — request a copy of the personal information we hold about you.</li>
          <li><strong>Correction</strong> — request that we correct inaccurate or incomplete information.</li>
          <li><strong>Deletion</strong> — request that we delete your personal information.</li>
          <li><strong>Portability</strong> — request your data in a structured, machine-readable format.</li>
          <li><strong>Objection</strong> — object to certain processing of your personal information.</li>
          <li><strong>Restriction</strong> — request that we restrict processing in certain circumstances.</li>
          <li><strong>Withdraw consent</strong> — where processing is based on consent, withdraw it at any time.</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:privacy@interlucent.ai" style={linkStyle}>privacy@interlucent.ai</a>. We will
          respond within 30 days, or as required by applicable law.
        </p>

        <h2 style={h2Style}>10. Children's Privacy</h2>
        <p>
          The Service is not directed at individuals under the age of 18. We do not knowingly collect
          personal information from children. If you believe a child has provided us with personal
          information, please contact us and we will delete it promptly.
        </p>

        <h2 style={h2Style}>11. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we will notify
          you via the email associated with your account or through a notice in the Service. Your continued
          use of the Service after the effective date constitutes acceptance of the updated policy.
        </p>

        <h2 style={h2Style}>12. Contact</h2>
        <p>
          If you have questions or concerns about this Privacy Policy or our data practices, please
          contact us at{' '}
          <a href="mailto:privacy@interlucent.ai" style={linkStyle}>privacy@interlucent.ai</a>.
        </p>
      </article>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#fafafa',
  display: 'flex',
  justifyContent: 'center',
  padding: '48px 24px',
  fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
};

const articleStyle: React.CSSProperties = {
  maxWidth: '720px',
  width: '100%',
  color: '#1a202c',
  fontSize: '15px',
  lineHeight: '1.7',
};

const h1Style: React.CSSProperties = {
  fontSize: '28px',
  fontWeight: 700,
  margin: '0 0 4px 0',
  letterSpacing: '-0.02em',
};

const metaStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#718096',
  margin: '0 0 32px 0',
};

const h2Style: React.CSSProperties = {
  fontSize: '17px',
  fontWeight: 600,
  margin: '28px 0 8px 0',
};

const h3Style: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  margin: '16px 0 4px 0',
};

const listStyle: React.CSSProperties = {
  paddingLeft: '24px',
  margin: '8px 0',
};

const linkStyle: React.CSSProperties = {
  color: '#667eea',
  textDecoration: 'none',
};
